<template>
  <section
    v-if="suggArray && Array.isArray(suggArray) && suggArray.length"
    class="base-suggested-estates"
  >
    <p class="text-subtitle text-bold text-black-monochrome">{{ getLabel }}</p>
    <div
      ref="scrollerRef"
      class="suggested-container"
      @scroll.passive="onScroll"
    >
      <listing-card
        v-for="item in visibleItems"
        :key="item.id"
        :estate="item"
        variant="tile"
        :property-type="props.type"
        source="suggestions"
      />
    </div>
    <div
      v-if="visibleItems.length > 1"
      class="suggested-dots"
      role="tablist"
      aria-label="Pagination"
    >
      <button
        v-for="(_, idx) in visibleItems"
        :key="idx"
        type="button"
        class="suggested-dot"
        :class="{ 'suggested-dot--active': activeIndex === idx }"
        :aria-label="`Go to card ${idx + 1}`"
        :aria-current="activeIndex === idx ? 'true' : undefined"
        @click="scrollToIndex(idx)"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import type { PropertyTypes } from "~/components/base/navigator/types";
import type { UpdatedUnitResponseDTO } from "~/store/unitPage";

interface IProps {
  estates: UpdatedUnitResponseDTO[];
  type: PropertyTypes;
}

const props = defineProps<IProps>();

const { t } = useI18n();

const suggArray = computed(() => {
  if (props.estates) {
    return props.estates.map((item) => {
      return {
        ...item,
        token: {
          ...item.token,
          issuer: {
            logo: item.provider?.logo,
            name: item.provider?.name,
            url: item.provider?.url,
          },
        },
      };
    });
  } else {
    return [];
  }
});

const visibleItems = computed(() => suggArray.value.slice(0, 3));

const getLabel = computed(() => {
  switch (props.type) {
    case "estates":
      return t("unit_page.un_sugg_estate");
    case "lands":
      return t("unit_page.un_sugg_land");
    default:
      return t("unit_page.un_sugg_estate");
  }
});

const scrollerRef = ref<HTMLDivElement | null>(null);
const activeIndex = ref(0);

const cardStep = () => {
  const el = scrollerRef.value;
  if (!el) return 0;
  const first = el.querySelector<HTMLElement>(":scope > *");
  if (!first) return 0;
  const gap = parseFloat(getComputedStyle(el).columnGap || "0") || 0;
  return first.getBoundingClientRect().width + gap;
};

const onScroll = () => {
  const el = scrollerRef.value;
  if (!el) return;
  const step = cardStep();
  if (step <= 0) return;
  activeIndex.value = Math.min(
    visibleItems.value.length - 1,
    Math.max(0, Math.round(el.scrollLeft / step))
  );
};

const scrollToIndex = (idx: number) => {
  const el = scrollerRef.value;
  if (!el) return;
  const step = cardStep();
  el.scrollTo({ left: idx * step, behavior: "smooth" });
};
</script>

<style scoped lang="scss">
.base-suggested-estates {
  position: relative;
  width: 100%;
  height: auto;
  display: grid;
  align-items: center;
  justify-items: flex-start;
  gap: 16px;
  max-width: 990px;
  margin: auto;
  p {
    justify-self: center;
  }
  .suggested-container {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
  }
}

/* Pagination dots only render on the mobile carousel (>1 card + horizontal
   scroll); hidden on the desktop 3-column grid where the cards are already
   visible side-by-side. */
.suggested-dots {
  display: none;
}

@media (max-width: 59rem) {
  .base-suggested-estates {
    /* Pull the carousel out of the wrapper's 16px gutter so we can re-introduce
       the gutter via scroll-padding on the scroller itself. Without this, the
       horizontal-scroll container clips card shadows on the left/right edges
       (overflow-x: auto forces overflow-y to "auto" per spec, so the visible
       hover-shadow would also be cut). */
    margin-left: -16px;
    margin-right: -16px;
    gap: 12px;
    .suggested-container {
      display: flex;
      flex-wrap: nowrap;
      grid-template-columns: none;
      overflow-x: auto;
      overflow-y: hidden;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      -ms-overflow-style: none;
      scroll-snap-type: x mandatory;
      gap: 12px;
      /* Padding gives the card's box-shadow (var(--shadow-card-hover)
         = up to 4px offset + 12px blur ≈ 16px reach) room on every side
         before the scroll-container's edge clips it. Horizontal padding
         doubles as the 16px viewport gutter that the negative margin
         above just removed. */
      padding: 8px 16px 20px;
      /* Without this, `scroll-snap-align: start` on the cards pulls the
         first card flush to the scroll container's left edge — which sits
         at the viewport edge thanks to the -16px margin — so the 16px
         padding-left collapses and the card looks "slightly hidden" on
         the left. scroll-padding-inline offsets the snap port by 16px
         on both ends so the first card snaps at +16px (matching the page
         gutter) and the last card has room on the right. */
      scroll-padding-inline: 16px;
      &::-webkit-scrollbar {
        display: none;
      }
      > * {
        flex: 0 0 280px;
        max-width: 280px;
        scroll-snap-align: start;
      }
    }
  }
  .suggested-dots {
    /* Section root uses `display: grid; justify-items: flex-start`, which
       defaults every grid item to its content width and pins it to the
       left — so without an explicit `justify-self` override the dot strip
       collapses to ~30px on the left edge. Stretch fills the grid track so
       the inner `justify-content: center` actually centers horizontally. */
    justify-self: stretch;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2px;
    margin-top: -8px;
  }
  .suggested-dot {
    appearance: none;
    border: none;
    background: var(--gray-monochrome-40);
    width: 6px;
    height: 6px;
    padding: 0;
    border-radius: 50%;
    cursor: pointer;
    transition:
      width 0.2s ease,
      border-radius 0.2s ease,
      background-color 0.2s ease;
    &--active {
      background: var(--black-monochrome-60);
      width: 16px;
      border-radius: 6px;
    }
  }
}
</style>
